import { useMemo, useRef, useState } from 'react';
import type { ComponentProps } from 'react';
import { LifeBuoy } from 'lucide-react';
import { can } from '@repo/shared-types';
import { Link, Outlet, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarRail,
  SidebarTrigger,
  useSidebar,
} from '@repo/ui/src/components/ui/sidebar';
import { LogoMark } from '@repo/ui/src/components/LogoMark';
import { NAV_GROUPS, filterNavForRole, isNavItemActive, type NavItemDef } from '../nav/navConfig';
import { readSidebarDefaultOpen } from '../nav/sidebarState';
import { useIsMobile } from '@repo/ui/src/hooks/use-mobile';
import { useAuthStore } from '../stores/authStore';
import { useCan } from '../hooks/useCan';
import { usePaymentsSummary } from '../hooks/usePayments';
import { useSubscription } from '../hooks/useSubscription';
import { useReconcileStatus } from '../hooks/useReconcileStatus';
import { NudgeBanner } from '../pages/billing/components/NudgeBanner';
import { OfferBanner } from '../pages/billing/components/OfferBanner';
import { CopilotPanel } from '../components/copilot/CopilotPanel';
import { useScrollRestoration } from '../hooks/useScrollRestoration';
import { DevClockWidget } from '../components/dev/DevClockWidget';
import { UpgradeDialog } from '../components/upgrade/UpgradeDialog';
import { HelpDialog } from '../components/help/HelpDialog';
import { InstallHelpDialog } from '../components/InstallHelpDialog';
import { useHelpDialogStore } from '../stores/helpDialogStore';
import { BottomTabBar } from '../components/mobile/BottomTabBar';
import { MoreSheet } from '../components/mobile/MoreSheet';
import { OrgMenu } from '../components/org/OrgMenu';
import { OrgAvatar } from '../components/org/OrgAvatar';
import { BranchScopeStrip } from '../components/branch/BranchScopeStrip';
import { BranchScopeRow } from '../components/branch/BranchScopeRow';

/** A nav Link that also closes the mobile Sheet after a tap — desktop
 *  (isMobile === false) is a no-op, so the same component works for the
 *  sidebar's nav links.
 *
 *  Since P0.4, nothing in the mobile top bar opens that Sheet any more —
 *  BottomTabBar + MoreSheet replaced it as phone navigation, and the
 *  hamburger that used to open it is gone. The `setOpenMobile(false)` call
 *  below is effectively dead on a phone (the Sidebar's own Cmd/Ctrl+B
 *  shortcut is the only thing left that can still open it). Left in place,
 *  not deleted: it's a harmless no-op, and this component is still the one
 *  actually rendering the desktop sidebar's nav links. */
function NavLinkClosingMobile({ to, children, onClick, ...rest }: ComponentProps<typeof Link>) {
  const { isMobile, setOpenMobile } = useSidebar();
  return (
    <Link
      to={to}
      {...rest}
      onClick={(e) => {
        onClick?.(e);
        if (isMobile) setOpenMobile(false);
      }}
    >
      {children}
    </Link>
  );
}

export function DashboardLayout() {
  const location = useLocation();
  const { t } = useTranslation('common');
  const { t: tBilling } = useTranslation('billing');
  const organization = useAuthStore((s) => s.organization);
  const role = useAuthStore((s) => s.role);
  // Gates MoreSheet/BottomTabBar mounting below.
  const isMobile = useIsMobile();
  const setHelpOpen = useHelpDialogStore((s) => s.setOpen);
  // Co-pilot rail is HIDDEN everywhere (Johnny, 2026-09-03): the students
  // co-pilot went stale while the product moved, and the AI surface is being
  // rethought as a global right sidebar (Trụ B) rather than per-section
  // panels. Code and API route stay in place for that redesign; flip this
  // back to the pathname/role predicate to resurrect the old rail.
  // Was: location.pathname.startsWith('/students') && !!role && can(role, 'manage:students')
  const hasCopilot = false;
  // Quota/billing is the owner's domain (Johnny, 2026-08-25): an assistant
  // teacher has no lever to pull on AI credits or reconcile quota, so the bars
  // are noise at best and a nudge to ask about the bill at worst.
  const canManageSettings = !!role && can(role, 'manage:settings');
  // Back/forward restores the content column's own scroll position — see the hook.
  const scrollRef = useRef<HTMLDivElement>(null);
  useScrollRestoration(scrollRef);
  // GET /payments/summary is gated manage:invoices server-side (Task 5); a
  // MEMBER's unconditional call 403ed and double-toasted on every page load
  // (retry: 1) — gate the fetch itself rather than swallow the error.
  const canBill = useCan('manage:invoices');
  const { data: paymentsSummary } = usePaymentsSummary(canBill);
  const unmatchedCount = paymentsSummary?.unmatchedPaymentsCount ?? 0;
  const { data: subscription } = useSubscription();
  // Gated the same as the bar it feeds below (canManageSettings) — a MEMBER
  // whose sidebar never renders the quota bar must not fire this fetch either
  // (THI-136).
  const { data: reconcileStatus } = useReconcileStatus(canManageSettings);
  const planTag =
    subscription?.status === 'TRIALING' ? tBilling('planBadge.trial') : subscription?.plan.name?.toUpperCase();

  // MEMBER (assistant teacher) only sees view + attendance pages — filter out
  // admin-only nav items, then drop any group left with no items rather than
  // rendering a bare group label.
  const navGroups = useMemo(() => filterNavForRole(NAV_GROUPS, role), [role]);
  const badgeCount = (b: NavItemDef['badge']) => (b === 'unmatchedPayments' && unmatchedCount > 0 ? unmatchedCount : undefined);
  const resolveLabel = (l: { ns: string; key: string }) => t(l.key, { ns: l.ns });

  // Read once: the cookie/viewport check that decides the initial rail state
  // shouldn't re-run on every render (it also touches document.cookie).
  const [defaultOpen] = useState(() => readSidebarDefaultOpen());
  // Phone-only: the Thêm sheet, opened from either the tab bar's "Thêm" tab
  // or the top bar's avatar button — both just flip this.
  const [moreOpen, setMoreOpen] = useState(false);

  return (
    // flex-1 min-h-0, not h-screen: AuthGuard now owns the h-screen viewport
    // box (it renders ImpersonationBanner above this layout's own Outlet
    // slot, so the banner also covers /onboarding — see M2 fix). h-screen
    // here would re-claim a full 100vh on top of whatever the banner already
    // takes, pushing this layout's own bottom below the fold and letting the
    // banner scroll away. min-h-0 overrides flex's default min-height: auto,
    // which would otherwise let this layout's content stretch the flex item
    // taller than the space actually available and leak scroll back up to
    // the body.
    <div className="flex flex-1 min-h-0 flex-col overflow-hidden bg-surface-50">
      <OfferBanner />
      <NudgeBanner />
      <SidebarProvider defaultOpen={defaultOpen} className="relative flex-1 min-h-0">
        <Sidebar collapsible="icon" className="absolute h-full">
          <SidebarHeader>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton size="lg" asChild tooltip="LopHoc.app">
                  <Link to="/">
                    <span className="flex size-10 shrink-0 items-center justify-center group-data-[collapsible=icon]:size-full">
                      <LogoMark className="size-full rounded-md" />
                    </span>
                    <div className="grid flex-1 text-left leading-tight group-data-[collapsible=icon]:hidden">
                      <span className="font-display truncate text-[15px] font-semibold text-surface-900">
                        LopHoc.app
                        {!!planTag && (
                          <span className="ml-1.5 rounded-sm bg-brand-500 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">
                            {planTag}
                          </span>
                        )}
                      </span>
                    </div>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarHeader>

          <BranchScopeRow />

          <SidebarContent role="navigation" aria-label={t('nav.mainNav')}>
            {navGroups.map((group) => (
              <SidebarGroup key={group.key}>
                <SidebarGroupLabel className="text-[11px] font-semibold uppercase tracking-wider text-surface-400">
                  {resolveLabel(group.label)}
                </SidebarGroupLabel>
                <SidebarMenu>
                  {group.items.map((item) => {
                    const active = isNavItemActive(item.href, location.pathname);
                    const count = badgeCount(item.badge);
                    return (
                      <SidebarMenuItem key={item.key}>
                        <SidebarMenuButton
                          asChild
                          isActive={active}
                          tooltip={resolveLabel(item.label)}
                          className="h-8 rounded-lg text-[13.5px] data-[active=true]:font-semibold"
                        >
                          <NavLinkClosingMobile to={item.href}>
                            <span className="relative flex size-4 shrink-0 items-center justify-center">
                              <item.icon className="size-4" strokeWidth={active ? 2 : 1.8} />
                              {count !== undefined && (
                                <span
                                  aria-hidden
                                  className="absolute -right-1 -top-1 hidden size-2 rounded-full bg-destructive group-data-[collapsible=icon]:block"
                                />
                              )}
                            </span>
                            <span className="flex-1 truncate group-data-[collapsible=icon]:hidden">{resolveLabel(item.label)}</span>
                          </NavLinkClosingMobile>
                        </SidebarMenuButton>
                        {count !== undefined && (
                          <SidebarMenuBadge className="h-5 min-w-5 rounded-full bg-destructive px-1.5 text-[11px] text-white group-data-[collapsible=icon]:hidden">
                            {count}
                          </SidebarMenuBadge>
                        )}
                      </SidebarMenuItem>
                    );
                  })}
                </SidebarMenu>
              </SidebarGroup>
            ))}
          </SidebarContent>

          <SidebarFooter>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton
                  tooltip={t('help.item')}
                  className="h-8 rounded-lg text-[13.5px]"
                  onClick={() => setHelpOpen(true)}
                >
                  <LifeBuoy className="size-4 shrink-0" strokeWidth={1.8} />
                  <span className="flex-1 truncate group-data-[collapsible=icon]:hidden">{t('help.item')}</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
            <div className="group-data-[collapsible=icon]:hidden">
              {canManageSettings && subscription?.usage && subscription.usage.aiCreditsLimit > 0 && (
                <div className="px-2 pb-2">
                  <div className="flex justify-between text-[11px] text-surface-400">
                    <span>{t('aiCredits.label')}</span>
                    <span className="tabular-nums">
                      {subscription.usage.aiCreditsUsed}/{subscription.usage.aiCreditsLimit}
                    </span>
                  </div>
                  <div className="mt-1 h-1 rounded-full bg-surface-100 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-brand-500"
                      style={{ width: `${Math.min(100, Math.round((subscription.usage.aiCreditsUsed / subscription.usage.aiCreditsLimit) * 100))}%` }}
                    />
                  </div>
                </div>
              )}
              {canManageSettings && reconcileStatus && !reconcileStatus.unlimited && reconcileStatus.quota > 0 && (
                <div className="px-2 pb-2">
                  <div className="flex justify-between text-[11px] text-surface-400">
                    <span>{t('reconcile.label')}</span>
                    <span className="tabular-nums">
                      {reconcileStatus.used}/{reconcileStatus.quota}
                    </span>
                  </div>
                  <div className="mt-1 h-1 rounded-full bg-surface-100 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-brand-500"
                      style={{ width: `${Math.min(100, Math.round((reconcileStatus.used / reconcileStatus.quota) * 100))}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
            <SidebarMenu>
              <SidebarMenuItem>
                <OrgMenu />
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarFooter>
          <SidebarRail />
        </Sidebar>

        <SidebarInset className="min-h-0 min-w-0 bg-surface-50">
          {/* Two columns: the page column (top bars + scroll area) and, where
              there is room, the co-pilot rail. The rail is a sibling of the
              whole column so it spans from the very top — before P2.4 it sat
              beside only the scroll area and started 48px down, under the
              desktop top bar. */}
          <div className="flex flex-1 min-h-0 min-w-0 overflow-hidden">
            <div className="flex flex-1 min-w-0 flex-col min-h-0">
              {/* Desktop/tablet top bar: the sidebar trigger lives here, flush with the
                  sidebar edge and outside the centered page container — shadcn's
                  sidebar-07 convention, per handoff 01-shell §2. */}
              <header className="hidden md:flex h-12 shrink-0 items-center gap-2 px-2">
                <SidebarTrigger aria-label={t('nav.toggleSidebar')} className="size-8" />
              </header>
              {/* Mobile top bar (brand + avatar) — hidden on md+. The hamburger is
                  gone: navigation on phones lives in BottomTabBar/MoreSheet below,
                  not the off-canvas Sidebar sheet, so there is nothing left for it
                  to open. The avatar opens the same Thêm sheet as the tab bar's
                  "Thêm" tab. */}
              <header className="md:hidden flex items-center gap-2 h-14 shrink-0 border-b border-surface-200 bg-surface-0 px-3">
                <Link to="/" className="flex items-center gap-2">
                  <LogoMark className="w-8 h-8" />
                  <span className="text-[15px] font-semibold tracking-tight text-surface-900">LopHoc.app</span>
                </Link>
                <button
                  type="button"
                  onClick={() => setMoreOpen(true)}
                  aria-label={t('orgMenu.trigger')}
                  className="ml-auto flex size-9 shrink-0 items-center justify-center"
                >
                  <OrgAvatar name={organization?.name} logoUrl={organization?.logoUrl} className="size-9" />
                </button>
              </header>
              <BranchScopeStrip />
              {/* Tab bar clearance on phones: BottomTabBar is fixed h-16 plus the
                  safe-area inset, so without this the last interactive thing on
                  every page — a pagination footer, a save button — parks
                  underneath it at full scroll. md+ has no tab bar, so it's just
                  a plain bottom breather there. */}
              <div ref={scrollRef} className="flex-1 flex flex-col min-w-0 min-h-0 overflow-auto pb-[calc(4rem+env(safe-area-inset-bottom))] md:pb-8">
                <Outlet />
              </div>
            </div>
            {/* Right rail — contextual co-pilot. Fixed 320px rail, so only mount it
                where there's room (xl+); below that it would crush the main column. */}
            {hasCopilot && (
              <div className="hidden xl:flex min-h-0">
                <CopilotPanel section="students" />
              </div>
            )}
          </div>

          {/* isMobile-gated, not just md:hidden: mounting these unconditionally
              would fire their attention/payments-summary queries from every
              desktop session too, for UI that never renders there. */}
          {isMobile && (
            <>
              <MoreSheet open={moreOpen} onOpenChange={setMoreOpen} />
              <BottomTabBar moreOpen={moreOpen} onOpenMore={() => setMoreOpen(true)} />
            </>
          )}
        </SidebarInset>
      </SidebarProvider>

      <UpgradeDialog />
      <HelpDialog />
      <InstallHelpDialog />
      <DevClockWidget />
    </div>
  );
}
